import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { MetricCard } from '../components/ui/metric-card';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
  Area,
  AreaChart,
} from 'recharts';
import { apiFetch } from '../utils/api';
import ReviewTicker from '../components/ReviewTicker';
import {
  Star,
  TrendingUp,
  TrendingDown,
  MessageSquare,
  AlertTriangle,
  Heart,
  Store,
  Trophy,
  AlertCircle,
  Brain,
  Activity,
  ThumbsUp,
  ThumbsDown,
  Clock,
  Zap,
  ChevronRight,
  Lightbulb,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CHART_COLORS = ['#2eb4ac', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#ec4899'];
const SENTIMENT_COLORS = { positive: '#2eb4ac', neutral: '#94a3b8', negative: '#ef4444' };

export default function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [ratingData, setRatingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brandLogos, setBrandLogos] = useState({});
  const [platformIcons, setPlatformIcons] = useState({});

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, trendRes, ratingRes, brandsRes, platformsRes] = await Promise.all([
        apiFetch('/api/dashboard/stats', { credentials: 'include' }),
        apiFetch('/api/analytics/sentiment-trend?days=30', { credentials: 'include' }),
        apiFetch('/api/analytics/rating-distribution', { credentials: 'include' }),
        apiFetch('/api/brands', { credentials: 'include' }),
        apiFetch('/api/platforms', { credentials: 'include' }),
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (trendRes.ok) setTrendData(await trendRes.json());
      if (ratingRes.ok) setRatingData(await ratingRes.json());
      if (brandsRes.ok) {
        const bl = await brandsRes.json();
        const logos = {};
        bl.forEach(b => { if (b.logo_url) logos[b.name] = b.logo_url; });
        setBrandLogos(logos);
      }
      if (platformsRes.ok) {
        const pl = await platformsRes.json();
        const icons = {};
        pl.forEach(p => { if (p.icon_url) icons[p.name] = p.icon_url; });
        setPlatformIcons(icons);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate metrics
  const totalReviews = stats?.total_reviews || 0;
  const avgRating = stats?.avg_rating || 0;
  const positiveCount = stats?.sentiment_distribution?.positive || 0;
  const negativeCount = stats?.sentiment_distribution?.negative || 0;
  const neutralCount = stats?.sentiment_distribution?.neutral || 0;
  const totalSentiment = positiveCount + negativeCount + neutralCount;
  const positivePercent = totalSentiment > 0 ? Math.round((positiveCount / totalSentiment) * 100) : 0;
  const negativePercent = totalSentiment > 0 ? Math.round((negativeCount / totalSentiment) * 100) : 0;
  const neutralPercent = totalSentiment > 0 ? Math.round((neutralCount / totalSentiment) * 100) : 0;

  // Platform data
  const platformData = stats?.platform_distribution
    ? Object.entries(stats.platform_distribution).map(([name, value], idx) => ({
        name,
        value,
        rating: stats.platform_ratings?.[name] || 0,
        color: CHART_COLORS[idx % CHART_COLORS.length],
      }))
    : [];

  // Sentiment pie data
  const sentimentPieData = [
    { name: 'Positive', value: positiveCount, color: SENTIMENT_COLORS.positive },
    { name: 'Neutral', value: neutralCount, color: SENTIMENT_COLORS.neutral },
    { name: 'Negative', value: negativeCount, color: SENTIMENT_COLORS.negative },
  ].filter(d => d.value > 0);

  // Review trend with total
  const reviewTrendData = trendData.map((item) => ({
    ...item,
    total: (item.positive || 0) + (item.neutral || 0) + (item.negative || 0),
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover border border-border rounded-lg shadow-lg p-3">
          <p className="font-medium text-sm mb-2">{label}</p>
          {payload.map((entry) => (
            <p key={entry.name} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i}><CardContent className="p-4"><Skeleton className="h-16 w-full" /></CardContent></Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-heading tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground mt-1">Complete overview of your review intelligence</p>
      </div>

      {/* Review history ticker */}
      <ReviewTicker />

      {/* Row 1: Primary KPIs - All Clickable */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <MetricCard
          metricKey="total_reviews"
          icon={MessageSquare}
          label="Analysed Reviews"
          value={totalReviews.toLocaleString()}
          subtext="All platforms"
          onClick={() => navigate('/reviews')}
        />
        <MetricCard
          metricKey="positive"
          icon={ThumbsUp}
          iconColor="text-[#2eb4ac]"
          label="Positive"
          value={positivePercent}
          suffix="%"
          subtext={`${positiveCount} reviews`}
          highlight={true}
          highlightColor="#2eb4ac"
          onClick={() => navigate('/reviews?sentiment=positive')}
        />
        <MetricCard
          metricKey="neutral"
          icon={Activity}
          iconColor="text-slate-500"
          label="Neutral"
          value={neutralPercent}
          suffix="%"
          subtext={`${neutralCount} reviews`}
          onClick={() => navigate('/reviews?sentiment=neutral')}
        />
        <MetricCard
          metricKey="negative"
          icon={ThumbsDown}
          iconColor="text-destructive"
          label="Negative"
          value={negativePercent}
          suffix="%"
          subtext={`${negativeCount} reviews`}
          onClick={() => navigate('/reviews?sentiment=negative')}
        />
      </div>

      {/* AI Summary - 1-2 liner review summary */}
      {stats?.ai_summary && (
        <Card className="card-hover bg-gradient-to-r from-[#8b5cf6]/5 via-[#2eb4ac]/5 to-transparent border-l-4 border-l-[#8b5cf6]" data-testid="ai-summary">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#8b5cf6]/10 flex items-center justify-center flex-shrink-0">
                <Brain className="w-4 h-4 text-[#8b5cf6]" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-semibold text-[#8b5cf6] mb-1 uppercase tracking-wide">AI Insight</p>
                <p className="text-sm text-foreground">
                  {totalReviews > 0 
                    ? `Analyzed ${totalReviews.toLocaleString()} reviews across ${stats?.brand_performance?.length || 0} brands. ${positivePercent}% positive sentiment with customers highlighting "${stats?.customers_love?.[0]?.topic || 'product quality'}" while "${stats?.customers_complain?.[0]?.topic || 'delivery'}" needs attention.`
                    : 'Upload reviews to get AI-powered insights and analysis.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Row 4: Rating & Platform Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Rating Distribution */}
        <Card className="card-hover" data-testid="chart-rating-dist">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading">⭐ Rating Distribution</CardTitle>
            <CardDescription>5★ to 1★ breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              {ratingData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={ratingData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                    <YAxis type="category" dataKey="rating" stroke="hsl(var(--muted-foreground))" fontSize={10} tickFormatter={(v) => `${v}★`} width={40} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" name="Reviews" radius={[0, 4, 4, 0]}>
                      {ratingData.map((entry, idx) => (
                        <Cell key={idx} fill={entry.rating >= 4 ? '#2eb4ac' : entry.rating >= 3 ? '#f59e0b' : '#ef4444'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground text-sm">No rating data</div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Platform Review Share & Rating */}
        <Card className="card-hover" data-testid="chart-platform">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading">🏪 Platform Comparison</CardTitle>
            <CardDescription>Review share & rating by marketplace</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              {platformData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={platformData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                    <YAxis yAxisId="left" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                    <YAxis yAxisId="right" orientation="right" stroke="hsl(var(--muted-foreground))" fontSize={10} domain={[0, 5]} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="value" name="Reviews" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="right" dataKey="rating" name="Avg Rating" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground text-sm">No platform data</div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 5: SKU Performance - Like other dashboards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performing SKUs */}
        <Card className="card-hover" data-testid="top-skus">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy className="w-4 h-4 text-[#f59e0b]" />
              Top Performing SKUs
            </CardTitle>
            <CardDescription>Highest ratings & sentiment</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats?.top_skus?.length > 0 ? (
                stats.top_skus.map((sku, idx) => (
                  <button
                    key={sku.product_name}
                    onClick={() => navigate(`/reviews?product=${encodeURIComponent(sku.product_name)}`)}
                    className="w-full flex items-center justify-between p-3 rounded-lg bg-[#2eb4ac]/5 hover:bg-[#2eb4ac]/10 transition-all cursor-pointer group text-left"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-xl">{idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : '📦'}</span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate max-w-[180px]">{sku.product_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {sku.total_reviews || sku.review_count || 0} reviews • {sku.positive_count || Math.round((sku.sentiment_score || 0) / 100 * (sku.total_reviews || 0))} positive
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {sku.avg_rating > 0 ? (
                        <Badge variant="secondary" className="bg-[#f59e0b]/10 text-[#f59e0b]">
                          <Star className="w-3 h-3 mr-1 fill-current" />{sku.avg_rating}
                        </Badge>
                      ) : (
                        <span className="text-[10px] text-muted-foreground/60 italic" data-testid={`not-live-yet-top-${sku.product_name}`}>Not Live Yet</span>
                      )}
                      <Badge variant="secondary" className="bg-[#2eb4ac]/10 text-[#2eb4ac]">
                        {sku.sentiment_score}%
                      </Badge>
                      <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </button>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">Analyze reviews to see top SKUs</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Underperforming SKUs */}
        <Card className="card-hover" data-testid="low-skus">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-destructive" />
              Underperforming SKUs
            </CardTitle>
            <CardDescription>Need attention - low ratings</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats?.underperforming_skus?.length > 0 ? (
                stats.underperforming_skus.map((sku) => (
                  <button
                    key={sku.product_name}
                    onClick={() => navigate(`/reviews?product=${encodeURIComponent(sku.product_name)}`)}
                    className="w-full flex items-center justify-between p-3 rounded-lg bg-destructive/5 hover:bg-destructive/10 transition-all cursor-pointer group text-left"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-xl">⚠️</span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate max-w-[180px]">{sku.product_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {sku.total_reviews || sku.review_count || 0} reviews • {sku.negative_count || 0} negative
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {sku.avg_rating > 0 ? (
                        <Badge variant="secondary" className="bg-[#f59e0b]/10 text-[#f59e0b]">
                          <Star className="w-3 h-3 mr-1 fill-current" />{sku.avg_rating}
                        </Badge>
                      ) : (
                        <span className="text-[10px] text-muted-foreground/60 italic" data-testid={`not-live-yet-under-${sku.product_name}`}>Not Live Yet</span>
                      )}
                      <Badge variant="secondary" className={`${sku.sentiment_score >= 40 ? 'bg-[#f59e0b]/10 text-[#f59e0b]' : 'bg-destructive/10 text-destructive'}`}>
                        {sku.sentiment_score || 0}%
                      </Badge>
                      <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </button>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No underperforming SKUs found</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 6: Issues & Recent Negative - All Clickable */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Trending Issues - Clickable */}
        <Card className="card-hover" data-testid="trending-issues">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-destructive" />
              Trending Issues
            </CardTitle>
            <CardDescription>Click to view related reviews</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats?.top_issues?.length > 0 ? (
                stats.top_issues.slice(0, 5).map((issue) => (
                  <button
                    key={issue.issue}
                    onClick={() => navigate(`/reviews?keyword=${encodeURIComponent(issue.issue)}&sentiment=negative`)}
                    className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-destructive/5 transition-colors cursor-pointer group"
                  >
                    <span className="text-sm capitalize">{issue.issue}</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="bg-destructive/10 text-destructive">{issue.count}</Badge>
                      <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </button>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No issues detected</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Trending Positive - Clickable → Positive Reviews */}
        <Card className="card-hover" data-testid="trending-positive">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Heart className="w-4 h-4 text-[#2eb4ac]" />
              Trending Positive
            </CardTitle>
            <CardDescription>Click to view related reviews</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats?.trending_keywords?.length > 0 ? (
                stats.trending_keywords.slice(0, 5).map((kw) => (
                  <button
                    key={kw.keyword}
                    onClick={() => navigate(`/reviews?keyword=${encodeURIComponent(kw.keyword)}&sentiment=positive`)}
                    className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-[#2eb4ac]/5 transition-colors cursor-pointer group"
                  >
                    <span className="text-sm capitalize">{kw.keyword}</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="bg-[#2eb4ac]/10 text-[#2eb4ac]">{kw.count}</Badge>
                      <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </button>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No positive trends yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Negative Reviews - Clickable */}
        <Card className="card-hover" data-testid="recent-negative">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4 text-destructive" />
              Recent Negative
            </CardTitle>
            <CardDescription>Click to view all negative reviews</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[180px]">
              <div className="space-y-3 pr-2">
                {stats?.recent_negative_reviews?.length > 0 ? (
                  stats.recent_negative_reviews.map((review) => (
                    <button
                      key={review.review_id}
                      onClick={() => navigate(`/reviews?sentiment=negative`)}
                      className="w-full text-left p-2 rounded-lg bg-destructive/5 border-l-2 border-destructive hover:bg-destructive/10 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-xs">
                          {platformIcons[review.platform] ? (
                            <img src={platformIcons[review.platform]} alt="" className="w-3 h-3 rounded object-cover mr-1" />
                          ) : null}
                          {review.platform}
                        </Badge>
                        <div className="flex">
                          {Array(5).fill(0).map((_, i) => (
                            <Star key={i} className={`w-3 h-3 ${i < review.rating ? 'text-[#f59e0b] fill-[#f59e0b]' : 'text-muted'}`} />
                          ))}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2">{review.review_text}</p>
                      <p className="text-xs text-muted-foreground mt-1">{review.product_name}</p>
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">No negative reviews</p>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Row 7: Brand Quick Access - Improved Design */}
      <Card className="card-hover border-t-2 border-t-[#8b5cf6]" data-testid="brand-quick-access">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Store className="w-4 h-4 text-[#8b5cf6]" />
                Brand Quick Access
              </CardTitle>
              <CardDescription>Click to view detailed brand dashboard</CardDescription>
            </div>
            <Badge variant="outline" className="text-xs">
              {stats?.brand_performance?.filter(b => b.brand && b.brand !== 'Unknown' && b.brand.trim() !== '').length || 0} brands
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {stats?.brand_performance?.filter(b => b.brand && b.brand !== 'Unknown' && b.brand.trim() !== '').length > 0 ? (
              stats.brand_performance
                .filter(b => b.brand && b.brand !== 'Unknown' && b.brand.trim() !== '')
                .map((brand) => (
                <div
                  key={brand.brand}
                  onClick={() => navigate(`/brand/${encodeURIComponent(brand.brand)}`)}
                  className="relative p-4 rounded-xl border border-border bg-gradient-to-br from-background to-accent/20 hover:border-[#8b5cf6] hover:shadow-lg hover:shadow-[#8b5cf6]/10 cursor-pointer transition-all duration-200 group"
                  data-testid={`brand-card-${brand.brand}`}
                >
                  {/* Brand Name & Arrow */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {brandLogos[brand.brand] ? (
                        <img src={brandLogos[brand.brand]} alt="" className="w-5 h-5 rounded object-cover flex-shrink-0" />
                      ) : null}
                      <h3 className="font-semibold text-sm truncate max-w-[120px] group-hover:text-[#8b5cf6] transition-colors">{brand.brand}</h3>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-[#8b5cf6] group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                  </div>
                  
                  {/* Stats Row */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex items-center gap-1 text-xs">
                      <Star className="w-3.5 h-3.5 text-[#f59e0b] fill-[#f59e0b]" />
                      <span className="font-medium">{brand.avg_rating}</span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {brand.total_reviews} reviews
                    </div>
                  </div>
                  
                  {/* Sentiment Bar */}
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Sentiment</span>
                      <span className={`font-medium ${brand.sentiment_score >= 60 ? 'text-[#2eb4ac]' : brand.sentiment_score >= 40 ? 'text-[#f59e0b]' : 'text-destructive'}`}>
                        {brand.sentiment_score}%
                      </span>
                    </div>
                    <div className="h-1.5 bg-muted/30 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all duration-500 ${brand.sentiment_score >= 60 ? 'bg-[#2eb4ac]' : brand.sentiment_score >= 40 ? 'bg-[#f59e0b]' : 'bg-destructive'}`}
                        style={{ width: `${brand.sentiment_score}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-full text-center py-8 text-muted-foreground">
                <Store className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No brands found. Upload reviews to see brand insights.</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
